import { forwardRef, useMemo, useState } from "react";
import {
  EditMSFilterSwitchSelectFieldPath,
  getMSFilterRenderedItemInitialProps,
  MSFilterRenderedItemProps,
  MSFilterSwitchSelectInitialProps,
  MSTableFilterItemRef,
} from "../../utils";
import {
  SHCheckbox,
  SHFormControlLabel,
  SHStack,
  SHToggleButtonOptions,
  SHTypography,
} from "@components/design-systems";
import { FormGroup, useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import { CheckSVG } from "@components/svgs";
import { Controller, useFormContext } from "react-hook-form";
import {
  ModelScreenerFilterDropdownLov,
  ModelScreenerFilterDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import { MSFilterItemLabel } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/label";
import { MSFilterResetIcon } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/reset";
import {
  getMSFilterSwitchSelectFormControlLabelSx,
  getMSFilterSwitchSelectToggleButtonSx,
  getMSFilterSwitchSelectToggleButtonWidth,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/switch-select/utils";
import { useMount, useToggle } from "react-use";
import { cloneDeep, isEqual } from "lodash";
import {
  MSFilterDefaultValueConstants,
  MSFilterSwitchOptions,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/constants";
import { useWindowWidth } from "@react-hook/window-size";

export const MSFilterSwitchSelect = forwardRef<
  MSTableFilterItemRef,
  MSFilterRenderedItemProps
>(({ item, itemSize, itemPaths, disabled }: MSFilterRenderedItemProps, ref) => {
  const { palette, shadows, ...otherThemeProps } = useTheme();
  const switchSelectFieldPaths = itemPaths as EditMSFilterSwitchSelectFieldPath;
  const isSmallSize = itemSize === "small";

  const { control, getValues, setValue } =
    useFormContext<ModelScreenerFilterDTO>();
  const { switchLabel, optionLabel, defaultSelectOptions } =
    getMSFilterRenderedItemInitialProps<MSFilterSwitchSelectInitialProps>(
      item,
    ) || {};

  const initialSelectedOptions = useMemo(() => {
    return defaultSelectOptions || [];
  }, [defaultSelectOptions]);

  const [isMounted, toggleMounted] = useToggle(false);
  const [currentSwitchValue, setCurrentSwitchValue] = useState<
    string | null | undefined
  >();
  const [currentSelectValue, setCurrentSelectValue] = useState<
    ModelScreenerFilterDropdownLov[]
  >([]);

  useMount(() => {
    let formSwitchValue = getValues(switchSelectFieldPaths.switchValue);
    const formSelectValue = getValues(switchSelectFieldPaths.lov);

    if (!formSwitchValue) {
      setValue(
        switchSelectFieldPaths.switchValue,
        MSFilterDefaultValueConstants.SwitchValue,
      );
      formSwitchValue = MSFilterDefaultValueConstants.SwitchValue;
    } else {
      formSwitchValue = String(formSwitchValue);
    }

    setCurrentSwitchValue(formSwitchValue);
    setCurrentSelectValue(
      formSelectValue
        ? (formSelectValue as ModelScreenerFilterDropdownLov[])
        : [],
    );
    toggleMounted(true);
  });

  const selectedValueMap: Record<number, boolean> = useMemo(() => {
    const map: Record<number, boolean> = {};
    currentSelectValue.forEach((opt, idx) => {
      map[idx] = !!opt.isSelected;
    });
    return map;
  }, [currentSelectValue]);

  const windowWidth = useWindowWidth();

  const handleOnSwitchChange = (
    value?: string | null,
  ): string | null | undefined => {
    if (value === currentSwitchValue || !value) return currentSwitchValue;
    setCurrentSwitchValue(value);
    return value;
  };

  const handleOnSelectValueChange = (
    index: number,
    isChecked: boolean,
  ): ModelScreenerFilterDropdownLov[] => {
    const clonedCurrent = cloneDeep(currentSelectValue) || [];
    const target = clonedCurrent[index];
    target.isSelected = !isChecked;
    setCurrentSelectValue(clonedCurrent);
    return clonedCurrent;
  };

  const handleOnReset = () => {
    setCurrentSwitchValue(MSFilterDefaultValueConstants.SwitchValue);
    setCurrentSelectValue(initialSelectedOptions);
    setValue(
      switchSelectFieldPaths.switchValue,
      MSFilterDefaultValueConstants.SwitchValue,
    );
    setValue(switchSelectFieldPaths.lov, initialSelectedOptions);
  };

  const shouldDisplayResetButton = useMemo(() => {
    return (
      !isEqual(currentSwitchValue, MSFilterDefaultValueConstants.SwitchValue) ||
      !isEqual(currentSelectValue, initialSelectedOptions)
    );
  }, [currentSelectValue, currentSwitchValue, initialSelectedOptions]);

  return (
    <SHStack
      ref={ref}
      padding={isSmallSize ? 1 : 1.5}
      bgcolor={palette.common.white}
      borderRadius={"12px"}
      boxShadow={shadows[1]}
      gap={1.5}
    >
      <SHStack
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <MSFilterItemLabel size={itemSize} disabled={disabled}>
          {switchLabel}
        </MSFilterItemLabel>
        {shouldDisplayResetButton && (
          <MSFilterResetIcon onClick={handleOnReset} disabled={disabled} />
        )}
      </SHStack>
      <SHStack
        width={"100%"}
        sx={getMSFilterSwitchSelectToggleButtonSx({
          theme: { palette, shadows, ...otherThemeProps },
          isSmallSize,
        })}
      >
        <Controller
          control={control}
          name={switchSelectFieldPaths.switchValue}
          render={({ field: { ref, ...other } }) => (
            <SHToggleButtonOptions
              disabled={disabled}
              size={itemSize}
              width={getMSFilterSwitchSelectToggleButtonWidth(windowWidth)}
              options={MSFilterSwitchOptions}
              value={currentSwitchValue}
              activeEndIcon={
                <CheckSVG
                  width={isSmallSize ? 15 : 19}
                  stroke={palette.common.white}
                />
              }
              onChange={(_, value) => {
                const newValue = handleOnSwitchChange(value);
                other.onChange(newValue);
              }}
              labelFontSize={isSmallSize ? 11 : 14}
              labelLineHeight={isSmallSize ? 0 : 1}
            />
          )}
        />
      </SHStack>

      <SHStack>
        <SHTypography
          fontSize={isSmallSize ? 10 : 12}
          color={hexToRGBA(palette.text.primary, 0.8)}
          textAlign={"left"}
          disabled={disabled}
        >
          {optionLabel}
        </SHTypography>

        <Controller
          control={control}
          name={switchSelectFieldPaths.lov}
          render={({ field: { ref, ...other } }) => (
            <FormGroup key={`${item.id}-${isMounted}`}>
              <SHStack overflow={"auto"} maxHeight={isSmallSize ? 180 : 310}>
                {initialSelectedOptions.map((option, index) => {
                  const isChecked = selectedValueMap[index] || false;
                  return (
                    <SHStack
                      key={`${index}-${isChecked}`}
                      direction="row"
                      bgcolor={
                        isChecked
                          ? hexToRGBA(palette.primary.main, 0.1)
                          : "transparent"
                      }
                      sx={{
                        "&:hover": {
                          backgroundColor: hexToRGBA(
                            palette.primary.main,
                            0.05,
                          ),
                        },
                        paddingTop: 0,
                      }}
                      paddingX={1}
                    >
                      <SHFormControlLabel
                        disabled={disabled}
                        sx={getMSFilterSwitchSelectFormControlLabelSx({
                          isSmallSize,
                        })}
                        control={
                          <SHCheckbox
                            disabled={disabled}
                            sx={{
                              "&:hover": {
                                backgroundColor: "transparent !important",
                              },
                            }}
                            size={itemSize}
                            checked={isChecked}
                            onChange={() => {
                              const newValue = handleOnSelectValueChange(
                                index,
                                isChecked,
                              );
                              other.onChange(newValue);
                            }}
                            value={option.value}
                          />
                        }
                        label={option.name}
                      />
                    </SHStack>
                  );
                })}
              </SHStack>
            </FormGroup>
          )}
        />
      </SHStack>
    </SHStack>
  );
});
